import type { User, UserRole } from '../types';

/**
 * Central place for role-based UI permissions.
 *
 * Roles:
 *  - owner     everything an admin can do PLUS the exclusive User Management tab
 *              (creating users, roles, and notification subscriptions)
 *  - admin     full access EXCEPT User Management
 *  - staff     manages inspections (create/edit/delete/batch/upload) and can see
 *              the Map and Unavailability, but NOT the Dashboard or User Management
 *  - inspector sees only their own assigned inspections
 */

/**
 * Whether this user can toggle between their own role and the inspector view.
 * Applies to owners, admins and staff flagged with `isAlsoInspector`.
 */
export function canSwitchToInspectorView(
  user: Pick<User, 'role' | 'isAlsoInspector'> | null | undefined
): boolean {
  if (!user) return false;
  return (user.role === 'owner' || user.role === 'admin' || user.role === 'staff') && Boolean(user.isAlsoInspector);
}

/**
 * Resolve the role actually in effect, accounting for admins and staff with
 * `isAlsoInspector` who toggled the header into "inspector" view mode.
 */
export function getEffectiveRole(
  user: Pick<User, 'role' | 'isAlsoInspector'> | null | undefined,
  viewMode?: string
): UserRole | undefined {
  if (!user) return undefined;
  if (canSwitchToInspectorView(user) && viewMode === 'inspector') {
    return 'inspector';
  }
  return user.role;
}

/**
 * Admin-level authority (Dashboard, calendar sync, CRM tools, full inspection
 * access). Owner is a superset of admin, so it passes here too.
 */
export const isAdmin = (role: UserRole | undefined): boolean =>
  role === 'admin' || role === 'owner';

/** Staff only. */
export const isStaff = (role: UserRole | undefined): boolean => role === 'staff';

/**
 * Can manage inspections: create, edit any field, batch update, delete,
 * bulk upload. True for owner, admin and staff.
 */
export const canManageInspections = (role: UserRole | undefined): boolean =>
  role === 'owner' || role === 'admin' || role === 'staff';

/**
 * Can open the Dashboard. Owners and admins get the company-wide view and
 * inspectors get their own. Staff has neither - unless it is a dual-role staff
 * member who switched into inspector view, in which case `role` is 'inspector'.
 */
export const canViewDashboard = (role: UserRole | undefined): boolean =>
  role === 'owner' || role === 'admin' || role === 'inspector';

/** Can open User Management. Owner only - this is the whole point of the role. */
export const canManageUsers = (role: UserRole | undefined): boolean => role === 'owner';

/**
 * Can trigger a manual Google Calendar sync. Open to the roles that schedule
 * work (owner, admin, staff) - staff coordinates inspections, so it needs to
 * push those changes to the calendars. Excluded for inspectors: a sync rewrites
 * every inspector's calendar, not just their own.
 *
 * Mirrors `authorize('admin', 'staff')` on /api/calendar.
 */
export const canSyncCalendar = (role: UserRole | undefined): boolean =>
  role === 'owner' || role === 'admin' || role === 'staff';

/**
 * Can see and manage every inspector's unavailability (vs. only their own).
 * Staff schedules inspections, so it needs the full availability picture.
 */
export const canManageAllUnavailability = (role: UserRole | undefined): boolean =>
  role === 'owner' || role === 'admin' || role === 'staff';

/** Landing route after login, based on role. */
export const getDefaultRoute = (role: UserRole | undefined): string =>
  canViewDashboard(role) ? '/dashboard' : '/inspections';
